/**
 * Blog Service - Firestore operations for blog management
 * Provides CRUD operations for blog posts in Geppetto admin
 */

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  serverTimestamp 
} from 'firebase/firestore';
import { getCurrentDb } from '../config/firebaseManager';

// Helper to get Firestore instance
const getDb = () => {
  const db = getCurrentDb();
  if (!db) {
    throw new Error('Firestore not initialized. Please select an environment.');
  }
  return db;
};

// Blog post interface
export interface BlogPost {
  id?: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string; // Rich text HTML
  featuredImage?: string;
  author: string;
  authorId: string;
  category: string;
  tags: string[];
  metaTitle?: string;
  metaDescription?: string;
  isPublished: boolean;
  publishedAt?: Timestamp;
  scheduledFor?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  viewCount?: number;
  readTimeMinutes?: number;
}

// Blog category interface
export interface BlogCategory {
  id?: string;
  name: string;
  slug: string;
  description?: string;
  color?: string;
  postCount: number;
  createdAt: Timestamp;
}

/**
 * Get all blog posts (with filters)
 */
export const getAllBlogPosts = async (filters?: {
  isPublished?: boolean;
  category?: string;
  limit?: number;
}): Promise<BlogPost[]> => {
  try {
    const postsRef = collection(getDb(), 'blogPosts');
    let q = query(postsRef, orderBy('createdAt', 'desc'));

    // Apply filters
    if (filters?.isPublished !== undefined) {
      q = query(q, where('isPublished', '==', filters.isPublished));
    }
    if (filters?.category) {
      q = query(q, where('category', '==', filters.category));
    }
    if (filters?.limit) {
      q = query(q, limit(filters.limit));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as BlogPost));
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

/**
 * Get single blog post by ID
 */
export const getBlogPostById = async (id: string): Promise<BlogPost | null> => {
  try {
    const docRef = doc(getDb(), 'blogPosts', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as BlogPost;
    }
    return null;
  } catch (error) {
    console.error('Error fetching blog post:', error);
    throw error;
  }
};

/**
 * Get blog post by slug (for frontend)
 */
export const getBlogPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  try {
    const postsRef = collection(getDb(), 'blogPosts');
    const q = query(postsRef, where('slug', '==', slug), limit(1));
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as BlogPost;
    }
    return null;
  } catch (error) {
    console.error('Error fetching blog post by slug:', error);
    throw error;
  }
};

/**
 * Create new blog post
 */
export const createBlogPost = async (post: Omit<BlogPost, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    const postsRef = collection(getDb(), 'blogPosts');
    const newPost = {
      ...post,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      viewCount: 0,
      readTimeMinutes: calculateReadTime(post.content),
      // Set publishedAt to serverTimestamp if published, null if draft
      publishedAt: post.isPublished ? serverTimestamp() : null
    };

    const docRef = await addDoc(postsRef, newPost);
    return docRef.id;
  } catch (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }
};

/**
 * Update existing blog post
 */
export const updateBlogPost = async (id: string, updates: Partial<BlogPost>): Promise<void> => {
  try {
    const docRef = doc(getDb(), 'blogPosts', id);
    const updateData: any = {
      ...updates,
      updatedAt: serverTimestamp(),
      readTimeMinutes: updates.content ? calculateReadTime(updates.content) : undefined
    };

    // Handle publishedAt when isPublished changes
    if ('isPublished' in updates) {
      updateData.publishedAt = updates.isPublished ? serverTimestamp() : null;
    }

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }
};

/**
 * Delete blog post
 */
export const deleteBlogPost = async (id: string): Promise<void> => {
  try {
    const docRef = doc(getDb(), 'blogPosts', id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
};

/**
 * Publish/unpublish blog post
 */
export const togglePublishBlogPost = async (id: string, isPublished: boolean): Promise<void> => {
  try {
    const docRef = doc(getDb(), 'blogPosts', id);
    await updateDoc(docRef, {
      isPublished,
      publishedAt: isPublished ? serverTimestamp() : null,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error toggling publish status:', error);
    throw error;
  }
};

/**
 * Increment view count
 */
export const incrementViewCount = async (id: string): Promise<void> => {
  try {
    const docRef = doc(getDb(), 'blogPosts', id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const currentViews = docSnap.data().viewCount || 0;
      await updateDoc(docRef, {
        viewCount: currentViews + 1
      });
    }
  } catch (error) {
    console.error('Error incrementing view count:', error);
    // Don't throw - view count is not critical
  }
};

/**
 * Get blog categories
 */
export const getBlogCategories = async (): Promise<BlogCategory[]> => {
  try {
    const categoriesRef = collection(getDb(), 'blogCategories');
    const q = query(categoriesRef, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as BlogCategory));
  } catch (error) {
    console.error('Error fetching blog categories:', error);
    throw error;
  }
};

/**
 * Create blog category
 */
export const createBlogCategory = async (category: Omit<BlogCategory, 'id' | 'createdAt' | 'postCount'>): Promise<string> => {
  try {
    const categoriesRef = collection(getDb(), 'blogCategories');
    const newCategory = {
      ...category,
      postCount: 0,
      createdAt: serverTimestamp()
    };

    const docRef = await addDoc(categoriesRef, newCategory);
    return docRef.id;
  } catch (error) {
    console.error('Error creating blog category:', error);
    throw error;
  }
};

/**
 * Update category post count
 */
export const updateCategoryPostCount = async (categoryName: string): Promise<void> => {
  try {
    // Count posts in this category
    const postsRef = collection(getDb(), 'blogPosts');
    const q = query(
      postsRef, 
      where('category', '==', categoryName),
      where('isPublished', '==', true)
    );
    const snapshot = await getDocs(q);
    const count = snapshot.size;

    // Update category
    const categoriesRef = collection(getDb(), 'blogCategories');
    const categoryQuery = query(categoriesRef, where('name', '==', categoryName), limit(1));
    const categorySnapshot = await getDocs(categoryQuery);
    
    if (!categorySnapshot.empty) {
      const categoryDoc = categorySnapshot.docs[0];
      await updateDoc(doc(getDb(), 'blogCategories', categoryDoc.id), {
        postCount: count
      });
    }
  } catch (error) {
    console.error('Error updating category post count:', error);
    // Don't throw - post count is not critical
  }
};

/**
 * Get related posts by category and tags
 */
export const getRelatedPosts = async (postId: string, category: string, tags: string[], limitCount: number = 3): Promise<BlogPost[]> => {
  try {
    const postsRef = collection(getDb(), 'blogPosts');
    
    // Try to find posts with matching tags first
    let q = query(
      postsRef,
      where('isPublished', '==', true),
      where('category', '==', category),
      orderBy('publishedAt', 'desc'),
      limit(limitCount + 1) // +1 because we'll filter out current post
    );

    const snapshot = await getDocs(q);
    const posts = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as BlogPost))
      .filter(post => post.id !== postId) // Exclude current post
      .slice(0, limitCount);

    return posts;
  } catch (error) {
    console.error('Error fetching related posts:', error);
    return [];
  }
};

/**
 * Search blog posts
 */
export const searchBlogPosts = async (searchTerm: string): Promise<BlogPost[]> => {
  try {
    // Note: Firestore doesn't support full-text search natively
    // This is a simple implementation - consider Algolia for production
    const postsRef = collection(getDb(), 'blogPosts');
    const q = query(postsRef, where('isPublished', '==', true));
    const snapshot = await getDocs(q);
    
    const allPosts = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as BlogPost));

    // Client-side filtering (simple search)
    const searchLower = searchTerm.toLowerCase();
    return allPosts.filter(post => 
      post.title.toLowerCase().includes(searchLower) ||
      post.excerpt.toLowerCase().includes(searchLower) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchLower))
    );
  } catch (error) {
    console.error('Error searching blog posts:', error);
    return [];
  }
};

/**
 * Generate unique slug from title
 */
export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
};

/**
 * Check if slug is unique
 */
export const isSlugUnique = async (slug: string, excludeId?: string): Promise<boolean> => {
  try {
    const postsRef = collection(getDb(), 'blogPosts');
    const q = query(postsRef, where('slug', '==', slug));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return true;
    
    // If editing existing post, check if slug belongs to same post
    if (excludeId) {
      return snapshot.docs.every(doc => doc.id === excludeId);
    }
    
    return false;
  } catch (error) {
    console.error('Error checking slug uniqueness:', error);
    return false;
  }
};

/**
 * Calculate estimated read time
 */
const calculateReadTime = (htmlContent: string): number => {
  // Strip HTML tags
  const text = htmlContent.replace(/<[^>]*>/g, '');
  const wordCount = text.split(/\s+/).length;
  const wordsPerMinute = 200;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return Math.max(1, minutes); // Minimum 1 minute
};

/**
 * Get blog statistics
 */
export const getBlogStats = async (): Promise<{
  totalPosts: number;
  publishedPosts: number;
  draftPosts: number;
  totalViews: number;
  categoriesCount: number;
}> => {
  try {
    const postsRef = collection(getDb(), 'blogPosts');
    const allPostsSnapshot = await getDocs(postsRef);
    
    const posts = allPostsSnapshot.docs.map(doc => doc.data() as BlogPost);
    const publishedPosts = posts.filter(p => p.isPublished);
    const draftPosts = posts.filter(p => !p.isPublished);
    const totalViews = posts.reduce((sum, p) => sum + (p.viewCount || 0), 0);

    const categoriesRef = collection(getDb(), 'blogCategories');
    const categoriesSnapshot = await getDocs(categoriesRef);

    return {
      totalPosts: posts.length,
      publishedPosts: publishedPosts.length,
      draftPosts: draftPosts.length,
      totalViews,
      categoriesCount: categoriesSnapshot.size
    };
  } catch (error) {
    console.error('Error fetching blog stats:', error);
    return {
      totalPosts: 0,
      publishedPosts: 0,
      draftPosts: 0,
      totalViews: 0,
      categoriesCount: 0
    };
  }
};
